// Owns the heatmap block state: how the image is being read, which clusters are drawn, which stat
// the answers list reports, the sort order of that list, and which of its rows are open. Seeded
// from the question's params and re-seeded whenever any of them changes outside the block.
//
// What makes this block different from the rest of the family is that part of its state is an
// input to the answers themselves: the cluster mask travels with the request, so settling on a new
// one is what makes the block refetch. The rules for when that happens are the coffee block's,
// gathered here rather than spread across half a dozen model listeners.

import { isEqual } from 'lodash'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import {
  applyHiddenStatus,
  calcClusters,
  calcClustersInConfig,
  hasClusters
} from '@aytm/app/stats/page/question-views/hm/lib/clusters'
import {
  calcHmSection,
  CLUSTER_VIEW_MODES,
  calcSortParam,
  GROUPING_DEFAULTS,
  isStoredGroupingDropped,
  isStoredViewDropped,
  resolveHmState,
  resolveSortOrder
} from '@aytm/app/stats/page/question-views/hm/lib/report-params'
import { clusterPosition } from '@aytm/app/stats/page/question-views/hm/lib/props/variants'
import { calcClustersSortOrder } from '@aytm/app/stats/page/question-views/hm/lib/table-utils'
import useLoadingRows from '@aytm/app/stats/page/question-views/components/items-list/useLoadingRows'

// the parts of the bag that decide which clusters are drawn rather than how they look
const GROUPING_KEYS = ['statType', 'blockType', 'stopWordLevel']

const resolveUncollapsed = section => (null == section ? '' : String(section))

// The open rows less those of the clusters a change took away, so a deleted cluster's row does not
// stay listed open with nothing to draw.
const dropRemovedClusterRows = (uncollapsed, before, after) => {
  const kept = new Set((after || []).map(cluster => clusterPosition(cluster.uid)))
  const removed = new Set(
    (before || []).map(cluster => clusterPosition(cluster.uid)).filter(position => !kept.has(position))
  )
  if (!removed.size) return uncollapsed

  return (uncollapsed || '')
    .split('.')
    .filter(position => position && !removed.has(position))
    .join('.')
}

export default function useHeatmapState({
  params,
  paramsRevision,
  questionKey,
  visualPosition,
  answer,
  onReady,
  onStateChange,
  onUpdateData,
  onAnswerVariantParamsChange,
  onAnswerVariantWcParamsChange,
  onAnswerVariantReady: announceAnswerVariantReady
}) {
  const contextRef = useRef()
  contextRef.current = { key: questionKey, visualPosition, answer }

  // Which view the block opens on is the answers' to decide -- a question that drew no contours
  // opens on its pins. A question still loading has no answers to decide with, and reads as one
  // that did draw contours, so the last settled ones are what the choice is made against.
  const settledAnswer = useRef(null)
  if ('loading' !== answer) settledAnswer.current = answer

  const paramsRef = useRef(params)
  paramsRef.current = params

  const seed = useCallback(source => {
    const { visualPosition, answer } = contextRef.current

    const hm = resolveHmState(source, settledAnswer.current, hasClusters(visualPosition))

    return {
      ...hm,
      sortOrder: resolveSortOrder(source.s),
      uncollapsed: resolveUncollapsed(source.cb),
      clusters: calcClusters({ visualPosition, answer, config: hm.config })
    }
    // everything it reads comes off the ref, so it never re-binds
  }, [])

  const [state, setState] = useState(() => seed(params || {}))

  const snapshot = useRef()
  snapshot.current = { state }

  // The loader the shell raises for a round trip it started and lowers when the answers are in.
  // Transient view state, held apart from the state the report params are written from, so
  // raising it announces nothing and a re-seed cannot drop it mid-fetch.
  const [loading, setLoading] = useState(false)

  // the rows whose own round trip is out
  const { loadingRows, markRowLoading, onRowsLoaded } = useLoadingRows({ questionKey, answer })

  // The mask the answers were last computed against; a new one is what the refetch is for.
  const reloadRef = useRef(false)

  const announceRefs = useRef()
  announceRefs.current = { onStateChange, onUpdateData, onAnswerVariantParamsChange, onAnswerVariantWcParamsChange }

  // Every open row hands its api up as it mounts; the block answers for them as well as for itself,
  // so the exporters reach the whole subtree through one call. The shell is told too -- the rest of
  // the page still walks it as a list of sub-item views.
  const announceAnswerVariantReadyRef = useRef(announceAnswerVariantReady)
  announceAnswerVariantReadyRef.current = announceAnswerVariantReady

  const answerVariantApis = useRef({})
  const onAnswerVariantReady = useCallback((position, answerVariantApi, meta) => {
    if (null == answerVariantApi) delete answerVariantApis.current[position]
    else answerVariantApis.current[position] = { api: answerVariantApi, meta: meta || {} }

    announceAnswerVariantReadyRef.current?.(position, answerVariantApi, meta)
  }, [])

  // A row announces its own changes the way any block does; this block is what writes them down, so
  // the announcement is forwarded with the key the params hang off. A way of reading its verbatims
  // that the payload cannot answer is a reload, named by the row it is for -- the block's own
  // reload names nothing, since it is the whole image being recomputed.
  const onAnswerVariantStateChange = useCallback(
    (position, key, reason, options = {}) => {
      const entry = answerVariantApis.current[position]
      if (!entry) return

      const reload =
        'mode' === reason ||
        ('seed' === reason && options.needsUpdate) ||
        ('state' === reason && options.configChanged) ||
        Boolean(options.resetAnswer)

      if (reload) markRowLoading(position)

      // the fetch is answered against the params, so a reload that follows a write travels with it
      // and the shell chains the two
      if ('seed' !== reason || options.needsUpdate) {
        announceRefs.current.onAnswerVariantParamsChange?.(key, entry.api.getReportParamsSection?.() || {}, reload)
      } else if (reload) {
        announceRefs.current.onUpdateData?.([key])
      }
    },
    [markRowLoading]
  )

  const onAnswerVariantWcParams = useCallback(
    (id, wcParams) => announceRefs.current.onAnswerVariantWcParamsChange?.(id, wcParams),
    []
  )

  // every open row, paired with whatever it answers - the ones that answer nothing drop out
  const gather = useCallback(
    read =>
      Object.entries(answerVariantApis.current).reduce((acc, [position, entry]) => {
        const value = read(entry.api, position, entry.meta)

        return null == value ? acc : acc.concat([[position, value]])
      }, []),
    []
  )

  const pendingReason = useRef('seed')

  const onModelChange = useCallback((change = {}) => {
    setState(current => {
      if (Object.entries(change).every(([key, value]) => isEqual(current[key], value))) return current

      let next = { ...current, ...change }

      if (Object.hasOwn(change, 'clusters')) {
        next.uncollapsed = dropRemovedClusterRows(next.uncollapsed, current.clusters, next.clusters)

        // Picking clusters is asking to see them, so Show Clusters follows the pick unless the
        // change names a mode itself. A grouping switches clusters on and off too, but reaches
        // here without a `clusters` key and so leaves the box as the researcher set it.
        if (!Object.hasOwn(change, 'clustersMode')) {
          const shown = !next.clusters?.length || next.clusters.some(cluster => !cluster.off)

          next.clustersMode = shown ? 'clusters-on' : 'clusters-off'
        }

        // with no clusters left the statistics-type control is hidden, so the list goes back to
        // reporting the pins by pin
        if (!next.clusters?.length) Object.assign(next, GROUPING_DEFAULTS)
      }

      // a grouping decides which clusters are drawn, so the mask they make is no longer the one the
      // answers were computed against
      if (GROUPING_KEYS.some(key => Object.hasOwn(change, key))) {
        next.clusters = applyHiddenStatus(next.clusters, next.blockType, next.stopWordLevel)
      }

      // The mask only feeds the picture where the researcher asked for it, or where the view is
      // drawn from the clusters rather than from the pins. Anywhere else a stale mask can wait --
      // and where it does feed it, it names the clusters as they stand, asking for it being itself
      // the moment they have to be named.
      const maskFeedsPicture = Boolean(next.config?.applyClustersMask) || CLUSTER_VIEW_MODES.includes(next.viewMode)

      if (maskFeedsPicture) {
        next = { ...next, config: calcClustersInConfig(next.clusters, next.config) }
      }

      if (!isEqual(next.config, current.config)) reloadRef.current = true

      pendingReason.current = 'state'

      return next
    })
  }, [])

  const api = useMemo(
    () => ({
      getState: () => ({ ...snapshot.current.state }),
      getReportParamsSection: () => {
        const { state } = snapshot.current

        return {
          ...calcHmSection(
            { ...state, config: calcClustersInConfig(state.clusters, state.config) },
            settledAnswer.current
          ),
          s: calcSortParam(state.sortOrder),
          cb: state.uncollapsed?.length ? state.uncollapsed : null
        }
      },
      // pptx / summary bridge: how the image is being read, with every open row spelled out --
      // the exporters read `items` off this
      getExportData: () => {
        const { state } = snapshot.current

        return {
          viewMode: state.viewMode,
          clustersMode: state.clustersMode,
          statType: state.statType,
          blockType: state.blockType,
          stopWordLevel: state.stopWordLevel,
          // the mask the answers are computed against, as the report params write it
          config: calcClustersInConfig(state.clusters, state.config),
          sortOrder: state.sortOrder,
          uncollapsed: state.uncollapsed,
          clusters: state.clusters,
          // the exports draw the cluster table from these alone; the pins table reads the batches
          ...('clusters' === state.statType
            ? { uidsSortOrder: calcClustersSortOrder({ clusters: state.clusters, sortOrder: state.sortOrder }) }
            : {}),
          items: gather((answerVariantApi, position, meta) => ({
            ...meta,
            ...(answerVariantApi.getExportData?.() || {})
          })).map(([position, model]) => ({ uid: model.uid ?? position, position, model }))
        }
      },
      // preset bridge: the param every cloud under this block would end up with if the preset were
      // poured into it. Resolving commits nothing; the shell writes the merged section down.
      getWCParam: preset =>
        gather(answerVariantApi =>
          answerVariantApi.getWcParamsSection?.(answerVariantApi.importWcPreset?.(preset))
        ).reduce((acc, [, section]) => Object.assign(acc, section), {}),
      update: (change = {}, options) => {
        // the loaders are the shell's own view state; everything else is the block's
        const { loading: nextLoading, loadedRows, ...rest } = change

        if (undefined !== nextLoading) setLoading(Boolean(nextLoading))
        if (loadedRows?.length) onRowsLoaded(loadedRows)
        if (Object.keys(rest).length) onModelChange(rest, options)
      }
    }),
    [gather, onModelChange, onRowsLoaded]
  )

  const onReadyRef = useRef(onReady)
  onReadyRef.current = onReady
  useEffect(() => {
    onReadyRef.current?.(api)
  }, [api])

  // a seed whose dropped view could not be written back until the answers arrived
  const viewWriteOwed = useRef(false)

  // A param moved outside the block (another view of the question, the URL, an undo): re-seed.
  //
  // Keyed on the revision the shell bumps per host render, not on the contents of the params bag.
  // A host-driven render is a fresh read of the live model; a re-render React starts for itself
  // carries the revision it already had. Keying on the contents missed an undo restoring a value
  // the block had already moved away from locally, because the block's own write is silent and the
  // string it was handed had never changed.
  useEffect(() => {
    const clustered = hasClusters(contextRef.current.visualPosition)

    // The view a dropped stored view falls back to is the answers' to decide, so while they are
    // loading its write waits for them.
    const viewDropped = isStoredViewDropped(params ?? {}, clustered)
    const answersLoading = 'loading' === contextRef.current.answer
    viewWriteOwed.current = viewDropped && answersLoading

    // a seed that had to drop a stored grouping or view writes the fallback back, as a change would
    pendingReason.current =
      isStoredGroupingDropped(params ?? {}, clustered) || (viewDropped && !answersLoading) ? 'state' : 'seed'

    setState(seed(params ?? {}))
  }, [params, paramsRevision, seed])

  // Fresh answers carry fresh cluster statistics, so the clusters are paired with them again.
  const lastAnswer = useRef(answer)
  useEffect(() => {
    if (lastAnswer.current === answer) return
    lastAnswer.current = answer

    if ('loading' === answer) return

    if (viewWriteOwed.current) {
      viewWriteOwed.current = false
      pendingReason.current = 'state'
    }

    setState(current => ({
      ...current,
      // the block may have opened before it had answers to choose a view with, so the choice is
      // remade now they are here -- a view the researcher stored is not a default and is left alone
      viewMode: resolveHmState(paramsRef.current || {}, answer, hasClusters(contextRef.current.visualPosition))
        .viewMode,
      clusters: calcClusters({ visualPosition: contextRef.current.visualPosition, answer, config: current.config })
    }))
  }, [answer, visualPosition])

  useEffect(() => {
    const reason = pendingReason.current
    pendingReason.current = null

    const reload = reloadRef.current
    reloadRef.current = false

    // the fetch is answered against the params, so a reload that follows a write travels with it
    // and the shell chains the two
    if (reason) announceRefs.current.onStateChange?.(reason, { reload })
    else if (reload) announceRefs.current.onUpdateData?.()
  }, [state])

  const onSortChange = useCallback(sortOrder => onModelChange({ sortOrder }), [onModelChange])

  const onToggle = useCallback(
    (position, collapsed) => {
      const open = new Set(snapshot.current.state.uncollapsed.split('.').filter(Boolean))

      if (collapsed) open.delete(String(position))
      else open.add(String(position))

      onModelChange({ uncollapsed: [...open].sort().join('.') })
    },
    [onModelChange]
  )

  const onToggleAll = useCallback(
    (collapse, positions = []) =>
      onModelChange({ uncollapsed: collapse ? '' : positions.map(String).sort().join('.') }),
    [onModelChange]
  )

  return {
    ...state,
    loading,
    loadingRows,
    onModelChange,
    onSortChange,
    onToggle,
    onToggleAll,
    onAnswerVariantReady,
    onAnswerVariantStateChange,
    onAnswerVariantWcParams
  }
}
